import {useContext, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {Alert, Card, Descriptions, Divider, Flex, Modal, Space, Steps, Tag, Typography,} from 'antd'
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CopyOutlined,
    ExclamationCircleOutlined,
    ExportOutlined,
    LinkOutlined,
    LoadingOutlined,
    LockOutlined,
    PlayCircleOutlined,
    RocketOutlined,
    StopOutlined,
    UserOutlined,
} from '@ant-design/icons'
import {NavContext} from '../../context/global_context'
import {getRequest, postRequest} from '../../services/base'
import {TRACER_PREVIEW, TRACER_PREVIEW_MGMT} from '../../constants/permission'
import Button from '../common/Button'
import StartTracerPreviewModal from './StartTracerPreviewModal'
import usePreviewTimer from './usePreviewTimer'

const { Text, Title } = Typography

const TracerPreviewPage = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [navState, navDispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()

  const [previewData, setPreviewData] = useState(null)
  const [branchNotFound, setBranchNotFound] = useState(false)
  const [showStartModal, setShowStartModal] = useState(false)

  const pollTimeoutRef = useRef(null)
  const dataVersion = searchParams.get('data_version') || ''

  // --- Derived state ---
  const tracerPreview = previewData?.tracer_preview || {}
  const {
    preview_building,
    preview_running,
    preview_deploying,
    preview_stopped,
    can_restart,
    in_use_by_other_make,
    tracer_credential,
  } = previewData || {}

  const hasMgmt = navState?.permissions?.includes(TRACER_PREVIEW_MGMT)
  const hasView = navState?.permissions?.includes(TRACER_PREVIEW)

  const isActive = preview_building || preview_deploying || preview_running
  const isStopped = !!preview_stopped

  // --- Timer ---
  const { buildTimer, runningTimer, hasBuildTime, hasRunningTime, clearTimers } =
    usePreviewTimer({
      previewId: tracerPreview?.preview_id,
      isBuilding: !!preview_building,
      isDeploying: !!preview_deploying,
      isRunning: !!preview_running,
      isActive: !!isActive,
    })

  // --- Steps state ---
  const getStepStatus = (index) => {
    if (preview_building) {
      return index === 0 ? 'process' : 'wait'
    }
    if (preview_deploying) {
      if (index === 0) return 'finish'
      if (index === 1) return 'process'
      return 'wait'
    }
    if (preview_running) {
      return index < 2 ? 'finish' : 'process'
    }
    return 'wait'
  }

  const steps = [
    {
      title: intl.formatMessage({ id: 'tracerPreview.stepBuildImage' }),
      icon:
        getStepStatus(0) === 'process' ? (
          <LoadingOutlined />
        ) : getStepStatus(0) === 'finish' ? (
          <CheckCircleOutlined />
        ) : undefined,
    },
    {
      title: intl.formatMessage({ id: 'tracerPreview.stepDeployService' }),
      icon:
        getStepStatus(1) === 'process' ? (
          <LoadingOutlined />
        ) : getStepStatus(1) === 'finish' ? (
          <CheckCircleOutlined />
        ) : undefined,
    },
    {
      title: intl.formatMessage({ id: 'tracerPreview.stepRunning' }),
      icon: getStepStatus(2) === 'process' ? <LoadingOutlined /> : undefined,
    },
  ]

  // --- API calls ---
  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'tracerPreview.pageHeader' }),
        sub_header: '',
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: '/vehicles',
        full_width_container: false,
      },
    })
    validateBranch()
    return () => clearTimeout(pollTimeoutRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateBranch = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'tracerPreview.validatingBranch' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(
      `/api/tracer/preview/validate/?data_version=${dataVersion}`
    )
    // Distinguish a failed request from a genuinely missing branch: the else
    // below says "branch not available in git", which is actively misleading
    // when the server simply could not be reached.
    if (res?.status === false) {
      navDispatch({ type: 'loader', data: {}, notification: {} })
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'tracerPreview.somethingWentWrong' }),
        },
      })
      return
    }
    if (res?.data) {
      navDispatch({
        type: 'header',
        data: {
          header: intl.formatMessage({ id: 'tracerPreview.pageHeader' }),
          sub_header: intl.formatMessage({ id: 'tracerPreview.pageSubHeader' }),
          id: '',
          show_back_btn: true,
          dtc_details: {},
          back_btn_url: '/vehicles',
          full_width_container: false,
        },
      })
      getCurrentPreview()
    } else {
      navDispatch({ type: 'loader', data: {}, notification: {} })
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({
            id: 'tracerPreview.branchNotAvailableNotify',
          }),
        },
      })
      setBranchNotFound(true)
      getCurrentPreview()
    }
  }

  const getCurrentPreview = async (isPolling = false) => {
    if (!isPolling) {
      navDispatch({
        type: 'loader',
        data: {
          caption: intl.formatMessage({ id: 'tracerPreview.loadingPreviewData' }),
          showLoader: true,
        },
        notification: {},
      })
    }
    let res = await getRequest(
      `/api/tracer/preview/preview/?data_version=${dataVersion}`
    )
    if (res?.status) {
      setPreviewData(res.data)
      if (!isPolling) {
        navDispatch({ type: 'loader', data: {}, notification: {} })
      }

      // Also poll while another portal holds the preview, so this page offers
      // a launch as soon as that preview stops.
      if (
        res?.data?.is_processing ||
        res?.data?.preview_building ||
        res?.data?.preview_deploying ||
        res?.data?.in_use_by_other_make
      ) {
        pollTimeoutRef.current = setTimeout(() => getCurrentPreview(true), 15000)
      }
    } else {
      if (branchNotFound) {
        navDispatch({ type: 'loader', data: {}, notification: {} })
        return
      }
      if (!isPolling) {
        navDispatch({ type: 'loader', data: {}, notification: {} })
      }
      navigate('/vehicles')
    }
  }

  const startTracerDeployment = () => {
    modal.confirm({
      title: intl.formatMessage(
        { id: 'tracerPreview.startTracer' },
        { vehicle: tracerPreview?.vehicle }
      ),
      icon: (
        <ExclamationCircleOutlined
          style={{ color: 'var(--color-active-primary)' }}
        />
      ),
      content: intl.formatMessage(
        { id: 'tracerPreview.areYouSureStart' },
        { vehicle: tracerPreview?.vehicle }
      ),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'forms.confirm' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage({
              id: 'tracerPreview.processingRequest',
            }),
            showLoader: true,
          },
          notification: {},
        })
        let res = await postRequest(`/api/tracer/preview/start/service/`, {
          preview_id: tracerPreview?.preview_id,
        })
        // Test the failure explicitly rather than inferring it from `data` being
        // absent, which would also report an error for a successful empty body.
        if (res?.status === false) {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({ id: 'tracerPreview.somethingWentWrong' }),
            },
          })
          return
        }
        if (res?.data) {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'success',
              msg: intl.formatMessage({ id: 'tracerPreview.successInitiated' }),
            },
          })
          getCurrentPreview()
        } else {
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg: intl.formatMessage({
                id: 'tracerPreview.somethingWentWrong',
              }),
            },
          })
        }
      },
    })
  }

  const stopTracerDeployment = () => {
    modal.confirm({
      title: intl.formatMessage(
        { id: 'tracerPreview.stopTracer' },
        { vehicle: tracerPreview?.vehicle }
      ),
      icon: (
        <ExclamationCircleOutlined
          style={{ color: 'var(--color-form-error-broder)' }}
        />
      ),
      content: intl.formatMessage(
        { id: 'tracerPreview.areYouSureStop' },
        { vehicle: tracerPreview?.vehicle }
      ),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'forms.confirm' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        navDispatch({
          type: 'loader',
          data: {
            caption: intl.formatMessage({
              id: 'tracerPreview.processingRequest',
            }),
            showLoader: true,
          },
          notification: {},
        })
        const previewId = tracerPreview?.preview_id
        const wasBuilding = !!preview_building
        let res = await postRequest(`/api/tracer/preview/stop/service/`, {
          preview_id: previewId,
        })
        // Clear the loader before notifying: the loader case replaces `notification`
        if (res?.status === false || !res?.data) {
          navDispatch({ type: 'loader', data: {}, notification: {} })
          navDispatch({
            type: 'notification',
            data: {
              notification_type: 'error',
              msg:
                res?.msg ||
                intl.formatMessage({ id: 'tracerPreview.somethingWentWrong' }),
            },
          })
          return
        }
        // The stopped preview no longer needs its polling or timer state
        clearTimeout(pollTimeoutRef.current)
        clearTimers(previewId)
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: intl.formatMessage({
              id: wasBuilding
                ? 'tracerPreview.previewCancelled'
                : 'tracerPreview.successInitiated',
            }),
          },
        })
        getCurrentPreview()
      },
    })
  }

  const openTracer = () => {
    modal.confirm({
      title: intl.formatMessage(
        { id: 'tracerPreview.openTracer' },
        { vehicle: tracerPreview?.vehicle }
      ),
      icon: (
        <ExclamationCircleOutlined
          style={{ color: 'var(--color-active-primary)' }}
        />
      ),
      content: intl.formatMessage(
        { id: 'tracerPreview.areYouSureOpen' },
        { vehicle: tracerPreview?.vehicle }
      ),
      centered: true,
      maskClosable: false,
      okText: intl.formatMessage({ id: 'forms.confirm' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: () => window.open(tracerPreview?.preview_url, '_blank'),
    })
  }

  const copyToClipboard = async (label, textToCopy) => {
    await navigator.clipboard.writeText(textToCopy)
    navDispatch({
      type: 'notification',
      data: {
        notification_type: 'info',
        msg: intl.formatMessage(
          { id: 'tracerPreview.copiedSuccessfully' },
          { label }
        ),
      },
    })
  }

  // --- Branch not found & no active preview ---
  if (branchNotFound && !isActive) {
    return (
      <div className="page tracer-preview">
        {contextHolder}
        <Flex justify="center" align="center" style={{ minHeight: '60vh' }}>
          <Card variant="outlined" className="tracer-preview-centered">
            <Flex vertical gap={16} align="center">
              <ExclamationCircleOutlined className="tracer-preview-warning-icon" />
              <Text strong>
                {intl.formatMessage({ id: 'tracerPreview.branchNotPushedTitle' })}
              </Text>
              <Text type="secondary">
                {intl.formatMessage({ id: 'tracerPreview.branchNotPushedSubTitle' })}
              </Text>
              <Button onClick={() => navigate('/vehicles')}>
                {intl.formatMessage({ id: 'tracerPreview.goBack' })}
              </Button>
            </Flex>
          </Card>
        </Flex>
      </div>
    )
  }

  // --- Preview held by another make's portal ---
  if (in_use_by_other_make) {
    return (
      <div className="page tracer-preview">
        {contextHolder}
        <Flex justify="center" align="center" style={{ minHeight: '60vh' }}>
          <Card variant="outlined" className="tracer-preview-centered">
            <Flex vertical gap={16} align="center">
              <ExclamationCircleOutlined className="tracer-preview-warning-icon" />
              <Text strong>
                {intl.formatMessage({ id: 'tracerPreview.previewInUseTitle' })}
              </Text>
              <Text type="secondary">
                {intl.formatMessage({ id: 'tracerPreview.previewInUseDesc' })}
              </Text>
              <Button onClick={() => navigate('/vehicles')}>
                {intl.formatMessage({ id: 'tracerPreview.goBack' })}
              </Button>
            </Flex>
          </Card>
        </Flex>
      </div>
    )
  }

  // --- STATE A: No active preview / Stopped ---
  const showLaunchCard = !isActive

  if (showLaunchCard) {
    return (
      <div className="page tracer-preview">
        {contextHolder}
        <Flex justify="center" align="center" style={{ minHeight: '60vh' }}>
          <Card
            variant="outlined"
            className="tracer-preview-centered"
          >
            <Space direction="vertical" size={16} align="center">
              <Flex align="center" justify="center" className="tracer-preview-launch-icon">
                <RocketOutlined />
              </Flex>
              <Title level={4} style={{ margin: 0 }}>
                {isStopped
                  ? intl.formatMessage({ id: 'tracerPreview.previewStopped' })
                  : intl.formatMessage({
                      id: 'tracerPreview.noActivePreview',
                    })}
              </Title>
              <Text type="secondary">
                {isStopped
                  ? intl.formatMessage({
                      id: 'tracerPreview.previewStoppedDesc',
                    })
                  : intl.formatMessage({
                      id: 'tracerPreview.noActivePreviewDesc',
                    })}
              </Text>
              {isStopped && (
                <Space wrap size={8} style={{ justifyContent: 'center' }}>
                  <Tag>{tracerPreview?.vehicle}</Tag>
                  {tracerPreview?.tracer_version && (
                    <Tag color="blue">{tracerPreview.tracer_version}</Tag>
                  )}
                  {tracerPreview?.data_version && (
                    <Tag color="purple">{tracerPreview.data_version}</Tag>
                  )}
                </Space>
              )}
              {isStopped && tracerPreview?.message && (
                <Text type="secondary">{tracerPreview.message}</Text>
              )}
              {hasMgmt && (
                <Flex gap={16} wrap justify="center">
                  {isStopped && can_restart && (
                    <Button
                      type="primary"
                      size="large"
                      icon={<PlayCircleOutlined />}
                      onClick={startTracerDeployment}
                    >
                      {intl.formatMessage({
                        id: 'tracerPreview.restartPreview',
                      })}
                    </Button>
                  )}
                  <Button
                    type={isStopped && can_restart ? 'default' : 'primary'}
                    size="large"
                    icon={<RocketOutlined />}
                    onClick={() => setShowStartModal(true)}
                  >
                    {isStopped
                      ? intl.formatMessage({
                          id: 'tracerPreview.launchNewPreview',
                        })
                      : intl.formatMessage({
                          id: 'tracerPreview.launchPreview',
                        })}
                  </Button>
                </Flex>
              )}
            </Space>
          </Card>
        </Flex>
        {showStartModal && (
          <StartTracerPreviewModal
            branch={previewData?.branch}
            onClose={() => setShowStartModal(false)}
          />
        )}
      </div>
    )
  }

  // --- STATE B: Active preview (building / deploying / running) ---
  return (
    <div className="page tracer-preview">
      {contextHolder}

      {branchNotFound && (
        <Alert
          type="warning"
          showIcon
          message={intl.formatMessage({ id: 'tracerPreview.branchDeletedRunning' })}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card variant="outlined">
        {/* Section: Preview Info */}
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Text strong>
            {intl.formatMessage({ id: 'tracerPreview.previewInfo' })}
          </Text>
          <Tag
            color={
              preview_running
                ? 'success'
                : preview_deploying
                  ? 'processing'
                  : 'warning'
            }
          >
            {preview_running
              ? intl.formatMessage({ id: 'tracerPreview.statusRunning' })
              : preview_deploying
                ? intl.formatMessage({ id: 'tracerPreview.statusDeploying' })
                : intl.formatMessage({ id: 'tracerPreview.statusBuilding' })}
          </Tag>
        </Flex>

        <Divider />

        {/* Details */}
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item
            label={intl.formatMessage({ id: 'tracerPreview.vehicle' })}
          >
            <Text strong>{tracerPreview?.vehicle || 'N/A'}</Text>
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({ id: 'tracerPreview.tracerVersion' })}
          >
            {tracerPreview?.tracer_version ? (
              <Tag color="blue">{tracerPreview.tracer_version}</Tag>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({ id: 'tracerPreview.dataVersion' })}
          >
            {tracerPreview?.data_version ? (
              <Tag color="purple">{tracerPreview.data_version}</Tag>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>
        </Descriptions>

        <Flex gap={24} wrap style={{ marginTop: 12 }}>
          {(hasBuildTime || preview_building || preview_deploying) && (
            <Space size={8}>
              <ClockCircleOutlined
                style={{ color: 'var(--color-warning-icon, #faad14)' }}
              />
              <Text type="secondary">
                {intl.formatMessage({ id: 'tracerPreview.buildTime' })}:
              </Text>
              <Text code>{buildTimer}</Text>
            </Space>
          )}
          {(hasRunningTime || preview_running) && (
            <Space size={8}>
              <ClockCircleOutlined
                style={{ color: 'var(--color-success-icon, #389e0d)' }}
              />
              <Text type="secondary">
                {intl.formatMessage({ id: 'tracerPreview.runningTime' })}:
              </Text>
              <Text code>{runningTimer}</Text>
            </Space>
          )}
        </Flex>

        {/* Credentials (when running) */}
        {preview_running && hasView && tracer_credential && (
          <>
            <Divider />

            <Flex justify="space-between" align="center" wrap gap={8}>
              <Text strong>
                {intl.formatMessage({ id: 'tracerPreview.credentials' })}
              </Text>
              <Tag color="success">
                <Space size={4}>
                  <CheckCircleOutlined />
                  {intl.formatMessage({ id: 'tracerPreview.active' })}
                </Space>
              </Tag>
            </Flex>

            <Divider />

            <Descriptions column={{ xs: 1, sm: 3 }} size="small">
              <Descriptions.Item
                label={
                  <Space size={4}>
                    <UserOutlined />
                    {intl.formatMessage({ id: 'tracerPreview.username' })}
                  </Space>
                }
              >
                <Space size={4}>
                  <Text code>{tracer_credential?.username}</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() =>
                      copyToClipboard(
                        intl.formatMessage({
                          id: 'tracerPreview.username',
                        }),
                        tracer_credential?.username
                      )
                    }
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Space size={4}>
                    <LockOutlined />
                    {intl.formatMessage({ id: 'tracerPreview.password' })}
                  </Space>
                }
              >
                <Space size={4}>
                  <Text code>{tracer_credential?.password}</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() =>
                      copyToClipboard(
                        intl.formatMessage({
                          id: 'tracerPreview.password',
                        }),
                        tracer_credential?.password
                      )
                    }
                  />
                </Space>
              </Descriptions.Item>
              {tracerPreview?.preview_url && (
                <Descriptions.Item
                  label={
                    <Space size={4}>
                      <LinkOutlined />
                      {intl.formatMessage({ id: 'tracerPreview.previewUrl' })}
                    </Space>
                  }
                >
                  <Typography.Link
                    onClick={(e) => { e.preventDefault(); openTracer() }}
                    style={{ wordBreak: 'break-all', cursor: 'pointer' }}
                  >
                    {tracerPreview.preview_url}
                  </Typography.Link>
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}

        {/* Section: Process Steps */}
        <Divider />

        <Text strong>
          {intl.formatMessage({ id: 'tracerPreview.processSteps' })}
        </Text>

        <Steps
          current={
            preview_running
              ? 2
              : preview_deploying
                ? 1
                : preview_building
                  ? 0
                  : -1
          }
          items={steps}
          style={{ marginTop: 16 }}
        />

        {/* Section: Actions / Cancel */}
        <Divider />

        {/* Actions (when running) */}
        {preview_running && (
          <Flex justify="flex-end" gap={16} wrap>
            {hasView && tracerPreview?.preview_url && (
              <Button
                type="primary"
                icon={<ExportOutlined />}
                onClick={openTracer}
              >
                {intl.formatMessage({ id: 'tracerPreview.openPreview' })}
              </Button>
            )}
            {hasMgmt && (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={stopTracerDeployment}
              >
                {intl.formatMessage({ id: 'tracerPreview.stopPreview' })}
              </Button>
            )}
            {hasMgmt && (
              <Button
                icon={<PlayCircleOutlined />}
                onClick={() => setShowStartModal(true)}
              >
                {intl.formatMessage({
                  id: 'tracerPreview.startForAnotherModel',
                })}
              </Button>
            )}
          </Flex>
        )}

        {/* Cancel (when building/deploying) */}
        {(preview_building || preview_deploying) && hasMgmt && (
          <Flex justify="space-between" align="center">
            {tracerPreview?.message && (
              <Text type="secondary">{tracerPreview.message}</Text>
            )}
            <Button
              danger
              icon={<StopOutlined />}
              onClick={stopTracerDeployment}
            >
              {intl.formatMessage({ id: 'tracerPreview.cancelPreview' })}
            </Button>
          </Flex>
        )}
      </Card>

      {showStartModal && (
        <StartTracerPreviewModal
          branch={previewData?.branch}
          onClose={() => setShowStartModal(false)}
        />
      )}
    </div>
  )
}

export default TracerPreviewPage
